/**
 * Units of measure: every unit the tenant may pick, by dimension, with its factor to the dimension's
 * base unit -- and, for an administrator, the tenant's own factors to declare.
 *
 * ED, 2026-10-30: "Build the unit conversion with base units per dimension." The list is the Unit of
 * Measure pick list; what this page adds is the conversion beside each unit (/t1/v1/units_of_measure).
 * The platform's factors are fixed and drawn as such. A tenant unit (a box, a reel, French gauge)
 * takes a factor here against the platform's base for its dimension, or -- for a dimension the
 * platform does not have -- is declared the base first. A unit with no factor is shown as not
 * converting, which is a real answer (a SET is however many the kit says), not a gap.
 *
 * UX ONLY ON THE GATE: the editor is drawn for `administrator` because the server refuses the save
 * without it; the list is for everyone, because the pick list is already in every form.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Box, Button, Checkbox, FormControlLabel, TextField, Typography} from "@mui/material";
import {AppContext} from "../util/AppContext";
import {makeCresAxios} from "../util/Axios";
import {hasPrivilege} from "../util/SessionContext";
import {Grey2, Grey6, Grey7, Grey8, Grey17} from "../theme/styles";

type Unit = {Value: string; Name?: string | null; Dimension?: string | null; Factor?: number | null; IsBase?: boolean;
             BaseUnit?: string | null; Convertible?: boolean; Platform?: boolean; USID?: string | null};
type Dimension = {Name: string; BaseUnit?: string | null};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

export default function UnitsOfMeasurePage() {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const administrator = hasPrivilege("administrator");
    const [units, setUnits] = useState<Unit[] | null>(null);
    const [dimensions, setDimensions] = useState<Dimension[]>([]);
    const [problem, setProblem] = useState<string | null>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [factor, setFactor] = useState<string>("");
    const [isBase, setIsBase] = useState<boolean>(false);
    const [busy, setBusy] = useState(false);

    const take = (response: any) => {
        const message = response?.data?.PlatformMessage ?? {};
        setUnits(listOf<Unit>(message?.ListOfUnitOfMeasure?.UnitOfMeasure));
        setDimensions(listOf<Dimension>(message?.ListOfDimension?.Dimension));
        setProblem(null);
    };
    const load = useCallback(() => {
        api.get("t1/v1/units_of_measure").then(take)
            .catch((failure: any) => { setUnits([]); setProblem(failure?.response?.data?.Cause ?? "The units could not be read."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { load(); }, [load]);

    const begin = (unit: Unit) => {
        setEditing(unit.Value);
        setFactor(unit.Factor == null ? "" : String(unit.Factor));
        setIsBase(!!unit.IsBase);
        setProblem(null);
    };
    const save = () => {
        if (!editing) return;
        setBusy(true);
        api.put("t1/v1/units_of_measure", {Unit: editing, Factor: isBase ? 1 : (factor.trim() === "" ? null : factor.trim()), IsBase: isBase})
            .then(response => { take(response); setEditing(null); })
            .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The conversion could not be saved."))
            .finally(() => setBusy(false));
    };
    const withdraw = (unit: Unit) => {
        setBusy(true);
        api.delete("t1/v1/units_of_measure", {params: {Unit: unit.Value}})
            .then(response => { take(response); setEditing(null); })
            .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The conversion could not be withdrawn."))
            .finally(() => setBusy(false));
    };

    const byDimension = new Map<string, Unit[]>();
    (units ?? []).forEach(unit => {
        const key = unit.Dimension ?? "—";
        byDimension.set(key, [...(byDimension.get(key) ?? []), unit]);
    });
    const baseOf = (name: string) => dimensions.find(d => d.Name === name)?.BaseUnit ?? null;
    const head = (text: string) => <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em"}}>{text}</Typography>;
    const cell = (text: React.ReactNode, color: string = Grey8) => <Typography sx={{fontSize: 13, color}}>{text}</Typography>;

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, height: "100%"}}>
            <div style={{flex: 1, overflow: "auto", padding: "16px 20px"}}>
                <Typography sx={{fontSize: 18, fontWeight: 600, color: Grey8, marginBottom: "4px"}}>Units of measure</Typography>
                <Typography sx={{fontSize: 13, color: Grey7, marginBottom: "14px", maxWidth: 820, lineHeight: 1.6}}>
                    Every unit a quantity may be stated in, by the dimension it measures. Each dimension has one base unit, and a unit's factor is how many of
                    that base one of it is: a kilogram is 1000 grams. Quantities convert through the base, so a bill of materials rolls up in it and lots held in
                    different units total in it. The platform's factors are fixed; {administrator
                        ? "your own units take a factor here, or are declared the base of a dimension of your own."
                        : "an administrator gives your own units their factors."} A unit with no factor does not convert, which is right for a set.
                </Typography>
                {problem && <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>}
                {units === null && <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>}
                {units && units.length === 0 && !problem && <Typography sx={{fontSize: 13, color: Grey7}}>No units of measure are defined.</Typography>}
                {Array.from(byDimension.entries()).map(([dimension, rows]) => (
                    <Box key={dimension} sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px", marginBottom: "12px", maxWidth: 980}}>
                        <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "8px"}}>
                            {dimension}
                            <Typography component="span" sx={{fontSize: 12.5, color: Grey7, marginLeft: "10px"}}>
                                {baseOf(dimension) ? `base unit ${baseOf(dimension)}` : "no base unit declared, so nothing in it converts"}
                            </Typography>
                        </Typography>
                        <Box sx={{display: "grid", gridTemplateColumns: "0.8fr 1.6fr 1.2fr 1fr 1fr 2fr", gap: "4px 12px", alignItems: "center"}}>
                            {["Unit", "Name", "Factor to base", "Base", "Source", ""].map((text, index) => <React.Fragment key={index}>{head(text)}</React.Fragment>)}
                            {rows.map(unit => {
                                const mine = editing === unit.Value;
                                return (
                                    <React.Fragment key={unit.Value}>
                                        {cell(unit.Value)}
                                        {cell(unit.Name ?? "—")}
                                        {mine ? (
                                            <TextField size="small" value={isBase ? "1" : factor} disabled={isBase} onChange={e => setFactor(e.target.value)}
                                                       placeholder="base units in one" slotProps={{htmlInput: {style: {fontSize: 13, padding: "4px 8px"}}}}/>
                                        ) : cell(unit.Convertible
                                            ? `${unit.IsBase ? 1 : unit.Factor} ${unit.BaseUnit}`
                                            : (unit.Factor != null ? `${unit.Factor} (no base yet)` : "does not convert"), unit.Convertible ? Grey17 : Grey6)}
                                        {mine ? (
                                            <FormControlLabel sx={{margin: 0}} label={<Typography sx={{fontSize: 12.5}}>Base</Typography>}
                                                              control={<Checkbox size="small" checked={isBase} onChange={e => setIsBase(e.target.checked)}/>}/>
                                        ) : cell(unit.IsBase ? "Base" : "")}
                                        {cell(unit.Platform ? "Platform" : "This tenant", unit.Platform ? Grey6 : Grey8)}
                                        <Box sx={{display: "flex", gap: "6px", justifyContent: "flex-end"}}>
                                            {administrator && !unit.Platform && !mine ? (
                                                <>
                                                    <Button size="small" variant="outlined" sx={{textTransform: "none"}} disabled={busy} onClick={() => begin(unit)}>
                                                        {unit.USID ? "Change" : "Give a factor"}
                                                    </Button>
                                                    {unit.USID ? <Button size="small" variant="text" sx={{textTransform: "none"}} disabled={busy} onClick={() => withdraw(unit)}>Withdraw</Button> : null}
                                                </>
                                            ) : null}
                                            {mine ? (
                                                <>
                                                    <Button size="small" variant="contained" disableElevation sx={{textTransform: "none"}} disabled={busy || (!isBase && factor.trim() === "")} onClick={save}>Save</Button>
                                                    <Button size="small" variant="text" sx={{textTransform: "none"}} disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
                                                </>
                                            ) : null}
                                        </Box>
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </div>
        </div>
    );
}
